import { Component, DoCheck, ElementRef, EventEmitter, Input, IterableDiffer, IterableDiffers, OnDestroy, Optional, Output, ViewChild } from '@angular/core';
import { FormsModule, NgModel } from '@angular/forms';
import { MatIconButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { MatError, MatFormField, MatInput, MatLabel, MatSuffix } from '@angular/material/input';
import { CustomInput } from 'components/input/custom-input/custom-input.component';
import { AutocompleteOffDirective } from 'directives/autocomplete-off.directive';
import { InputStateDirective } from 'directives/input-state.directive';
import { ValidationDirective } from 'directives/validation.directive';
import { SelectListItemModel } from 'models/SelectList';
import { TranslatePipe } from 'pipes/translate.pipe';
import { InputContainerService } from 'services/input-container.service';
import { DropdownInputDirective } from './dropdown-input.directive';

@Component({
	selector: 'custom-select',
	templateUrl: './custom-select.component.html',
	imports: [MatFormField, MatLabel, InputStateDirective, MatInput, FormsModule, ValidationDirective, AutocompleteOffDirective, DropdownInputDirective, MatError, MatIconButton, MatSuffix, MatIcon, TranslatePipe]
})
export class CustomSelectComponent implements OnDestroy, DoCheck, CustomInput {
	@ViewChild('input') input?: ElementRef<HTMLInputElement>;
	@ViewChild(NgModel) ngModel?: NgModel;

	@Input() selectList: SelectListItemModel[] = [];
	@Input() label = '';
	@Input() placeholder = '';
	@Input() charactersForSuggestions = 0;
	@Input() required = false;
	@Input() autoSort = false; // Used in dropdown-list.ts
	@Input() allowAutoFocus = true;
	@Input() disabled = false;
	@Output() focusedChange = new EventEmitter<boolean>();
	@Output() valueChange = new EventEmitter<any>();

	public selectedItem: SelectListItemModel | null = null;
	public displayValue = '';
	public pristine = true;

	private defaultIterableDiffer: IterableDiffer<any>;
	private _value?: string | number | object;
	@Input()
	public set value(value: string | number | object | undefined) {
		if (this.selectList.length == 0)
			this._value = value; // Save value for use in ngDoCheck(), in case value is set before selectList has items.
		else
			this.selectItem(value !== undefined ? this.getSelectedItemFromValue(value) : null, false);
	}
	public get value() {
		return this._value;
	}

	constructor(
		private iterableDiffers: IterableDiffers,
		public readonly element: ElementRef<HTMLElement>,
		@Optional() private inputContainer: InputContainerService) {
		this.defaultIterableDiffer = this.iterableDiffers.find(this.selectList).create() as IterableDiffer<any>;
		if (this.inputContainer) this.inputContainer.add(this);
	}

	public selectItem(item: SelectListItemModel | null, userUpdated = true) {
		const value = item ? item.value : undefined;

		this.selectedItem = item;
		this.refreshDisplayValue();

		this._value = value;

		if (userUpdated) {
			this.valueChange.emit(value);
			this.input?.nativeElement.blur();
			this.pristine = false;
			this.ngModel?.control.markAsDirty();
		}
	}

	ngOnDestroy() {
		if (this.inputContainer) this.inputContainer.remove(this);
	}

	ngDoCheck() {
		let change = this.defaultIterableDiffer.diff(this.selectList);
		if (change) {
			this.selectItem(this.getSelectedItemFromValue(this.value), false);
		}
	}

	setFocus(autoFocus?: boolean) {
		if (autoFocus && !this.allowAutoFocus) return;
		setTimeout(() => {
			if (this.input) this.input.nativeElement.focus();
		});
	}
	focused() {
		this.focusedChange.emit(true);
	}
	blurred() {
		this.focusedChange.emit(false);
		this.refreshDisplayValue();
	}

	getSelectedItemFromValue(value?: string | number | object): SelectListItemModel | null {
		if (value === undefined) return null;
		return this.selectList.find(item => item.value == value) ?? null;
	}

	refreshDisplayValue() {
		this.displayValue = this.selectedItem?.name ?? '';
	}

	resetInput() {
		this.selectItem(null);
	}
}

